import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Mail, Bell, MessageSquare, Loader2, CheckCircle2, ArrowLeft, Shield } from "lucide-react";

const KIND_LABEL_FALLBACK = {
  signup:           "Confirmation d'inscription",
  password_reset:   "Réinitialisation de mot de passe",
  mission_proposed: "Nouvelle mission proposée",
  mission_response: "Acceptation/refus de mission",
  invoice:          "Facture",
  weekly_digest:    "Récapitulatif hebdomadaire",
  need_match:       "Nouveau besoin compatible",
};

// Channels users can opt out of (signup/password_reset are always sent)
const TRANSACTIONAL = new Set(["signup", "password_reset"]);

const CHANNELS = [
  { key: "email",  label: "Email",  icon: Mail },
  { key: "in_app", label: "Notif",  icon: Bell },
  { key: "sms",    label: "SMS",    icon: MessageSquare, disabled: true, badge: "Bientôt" },
];

export default function Preferences() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [kinds, setKinds] = useState(KIND_LABEL_FALLBACK);
  const [prefs, setPrefs] = useState(user?.notification_preferences || {});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications/kinds");
        if (data?.kinds) setKinds(data.kinds);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { setPrefs(user?.notification_preferences || {}); }, [user?.notification_preferences]);

  const getPref = (kind, channel) => {
    const k = prefs[kind];
    if (!k) return true; // default ON
    return !!k[channel];
  };
  const setPref = (kind, channel, value) => {
    setPrefs((prev) => {
      const next = { ...(prev || {}) };
      const cur = { email: true, in_app: true, sms: false, ...(next[kind] || {}) };
      cur[channel] = value;
      next[kind] = cur;
      return next;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/auth/profile", { notification_preferences: prefs });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } finally { setBusy(false); }
  };

  const allOn = (channel) => {
    const next = { ...(prefs || {}) };
    for (const k of Object.keys(kinds)) {
      const cur = { email: true, in_app: true, sms: false, ...(next[k] || {}) };
      cur[channel] = true;
      next[k] = cur;
    }
    setPrefs(next);
  };
  const allOff = (channel) => {
    const next = { ...(prefs || {}) };
    for (const k of Object.keys(kinds)) {
      if (TRANSACTIONAL.has(k) && channel === "email") continue; // keep transactional emails
      const cur = { email: true, in_app: true, sms: false, ...(next[k] || {}) };
      cur[channel] = false;
      next[k] = cur;
    }
    setPrefs(next);
  };

  return (
    <div className="max-w-4xl mx-auto p-4 lg:p-8 pb-24 lg:pb-10">
      <button onClick={() => nav("/profile")} className="text-xs text-[#6B726A] hover:text-[#1A4D2E] inline-flex items-center gap-1 mb-3" data-testid="back-to-profile">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour au profil
      </button>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0F2A1B]" style={{ fontFamily: "Outfit" }}>Préférences de notifications</h1>
          <p className="text-sm text-[#6B726A] mt-1">Choisissez comment vous souhaitez être averti pour chaque type d&apos;événement.</p>
        </div>
        <button onClick={save} disabled={busy} data-testid="prefs-save"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1A4D2E] text-white text-sm font-medium hover:bg-[#143C24] disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Enregistrer
        </button>
        {saved && <span className="text-xs text-[#1A4D2E] inline-flex items-center gap-1" data-testid="prefs-saved"><CheckCircle2 className="w-3 h-3" /> Enregistré</span>}
      </div>

      {/* Bulk actions */}
      <div className="bg-white rounded-2xl border border-[#E2E5E0] p-4 mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-[#6B726A] mr-2">Tout activer :</span>
        {CHANNELS.filter(c => !c.disabled).map((c) => (
          <button key={`on-${c.key}`} onClick={() => allOn(c.key)} data-testid={`bulk-on-${c.key}`}
            className="text-xs px-3 py-1.5 rounded-full border border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#E8F0EA]">
            ✓ {c.label}
          </button>
        ))}
        <span className="text-xs text-[#6B726A] ml-3 mr-2">Tout désactiver :</span>
        {CHANNELS.filter(c => !c.disabled).map((c) => (
          <button key={`off-${c.key}`} onClick={() => allOff(c.key)} data-testid={`bulk-off-${c.key}`}
            className="text-xs px-3 py-1.5 rounded-full border border-[#E2E5E0] text-[#6B726A] hover:bg-[#F7F8F6]">
            ✕ {c.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E2E5E0] overflow-hidden">
        <div className="grid grid-cols-[1fr_repeat(3,90px)] items-center px-5 py-3 border-b border-[#E2E5E0] bg-[#F7F8F6]">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A]">Événement</div>
          {CHANNELS.map((c) => (
            <div key={c.key} className="text-[11px] uppercase tracking-wider font-semibold text-[#6B726A] text-center inline-flex items-center justify-center gap-1">
              <c.icon className="w-3 h-3" /> {c.label}
              {c.badge && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F4F0FF] text-[#6B4DD9] border border-[#D9CCFB]">{c.badge}</span>}
            </div>
          ))}
        </div>
        {Object.entries(kinds).map(([kind, label]) => {
          const locked = TRANSACTIONAL.has(kind);
          return (
            <div key={kind} className="grid grid-cols-[1fr_repeat(3,90px)] items-center px-5 py-3 border-b last:border-b-0 border-[#F0F2EF]" data-testid={`pref-row-${kind}`}>
              <div className="min-w-0">
                <div className="text-sm text-[#0F2A1B] font-medium">{label}</div>
                {locked && (
                  <div className="text-[10px] text-[#6B726A] inline-flex items-center gap-1 mt-0.5">
                    <Shield className="w-3 h-3" /> Email transactionnel obligatoire
                  </div>
                )}
              </div>
              {CHANNELS.map((c) => {
                const channelLocked = c.disabled || (locked && c.key === "email");
                const checked = channelLocked ? (c.key === "email" && locked ? true : false) : getPref(kind, c.key);
                return (
                  <div key={c.key} className="text-center">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={checked}
                      disabled={channelLocked}
                      onClick={() => setPref(kind, c.key, !checked)}
                      data-testid={`toggle-${kind}-${c.key}`}
                      className={`w-10 h-6 rounded-full relative transition-colors ${checked ? "bg-[#1A4D2E]" : "bg-[#E2E5E0]"} ${channelLocked ? "opacity-50 cursor-not-allowed" : "hover:brightness-95"}`}>
                      <span className={`absolute top-0.5 ${checked ? "left-[18px]" : "left-0.5"} w-5 h-5 bg-white rounded-full shadow transition-all`} />
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-[#FFF8E6] border border-[#F2C46B]/40 rounded-2xl p-4 text-xs text-[#7a5b18]">
        🔒 Vos préférences sont stockées chiffrées. Vous pouvez à tout moment révoquer un canal. Les emails transactionnels (inscription, mot de passe) restent obligatoires pour la sécurité de votre compte.
      </div>
    </div>
  );
}
